import { useRef } from 'react'

import wrapPromise from './wrapPromise'
// #253: the cache registry lives apart so the error boundary can clear these
// without importing the Firestore layer into the main bundle.
import { registerCache } from './dataCaches'
import { getCollection, getCollectionFromServer, getCollectionInYears } from './firebase/collections'
import { getDocument, getDocumentFromServer, setDocument, updateDocument, deleteDocument } from './firebase/documents'
import firebase from './firebase/app'

const getDataFragments = (paths, forceServer = false) => {
  return Promise.all(
  paths
    .map(path => getDataFragment(
      Array.isArray(path)
        ? path
        : path?.split('/') ?? [],
      forceServer
    ))
)
}

const isCollectionPath = path => path
  .filter(part => part)
  .length % 2

// A forced-server read (getDocFromServer / getDocsFromServer) has no offline
// fallback of its own: one flaky moment rejects it outright. The report screens
// are the only ones that force the server, so that rejection used to reach the
// error boundary as "אופס! משהו השתבש" while every other screen — reading the
// same documents through the cache-backed getters — kept working. Fall back to
// the cached read instead: stale-but-real data beats a broken screen.
const withCacheFallback = (serverGetter, cachedGetter) => (...args) =>
  Promise.resolve(serverGetter(...args))
    .catch(error => {
      console.warn('server read failed, falling back to the local cache', args, error)
      return cachedGetter(...args)
    })

const getDataFragment = (path, forceServer = false) => {
  const [fromServer, fromCache] = isCollectionPath(path)
    ? [getCollectionFromServer, getCollection]
    : [getDocumentFromServer, getDocument]

  return forceServer
    ? withCacheFallback(fromServer, fromCache)(...path)
    : fromCache(...path)
}

const wrapData = (getter, enhance = x => x, cache = {}) => function wrapData(...pathParts) {
  const path = pathParts.join('/')

  evictFailed(cache, path)
  return cache[path] || (cache[path] = enhance(wrapPromise(getter(path))))
}
const wrapDataRefresh = (getter, enhance = x => x, cache = {}) => function wrapData(...pathParts) {
  const path = pathParts.join('/')

  checkCache(cache, path)
  return cache[path] || (cache[path] = enhance(wrapPromise(getter(path))))
}

export { clearDataCaches } from './dataCaches'

export const wrapCollection = wrapData(getCollection, undefined, registerCache())
export const wrapCollectionFromServer = wrapData(withCacheFallback(getCollectionFromServer, getCollection), undefined, registerCache())

// #902 — same suspense-cache shape as wrapCollection above, but keyed
// additionally on the year window: switching the viewed month to a
// different set of years must not reuse a cached read (and the differently
// -scoped live listener built from it) that answers for the wrong years.
const collectionInYearsCache = registerCache()
export const wrapCollectionInYears = (years, ...pathParts) => {
  const cacheKey = [...pathParts, ...years].join('/')

  evictFailed(collectionInYearsCache, cacheKey)
  return collectionInYearsCache[cacheKey]
    || (collectionInYearsCache[cacheKey] = wrapPromise(getCollectionInYears(pathParts, years)))
}
export const wrapUserCollection = (...pathParts) => wrapCollection(
  'users',
  firebase.auth().currentUser.uid,
  ...pathParts,
)

// #846: kept as named bindings (not inline registerCache() calls) so
// useEnhanceDocument's refetch() below can reach into the SAME cache objects
// that wrapDocument/wrapDocumentRefresh read from.
const documentCache = registerCache()
const documentRefreshCache = registerCache()

export const wrapDocument = wrapData(getDocument, wrapper => {
  return {
    ...wrapper,
    useRead: useRead.bind(null, wrapper.promise),
  }
}, documentCache)
export const wrapDocumentRefresh = wrapDataRefresh(getDocument, wrapper => {
  return {
    ...wrapper,
    useRead: useRead.bind(null, wrapper.promise),
  }
}, documentRefreshCache)

// #846: wrapDocument/wrapDocumentRefresh cache a document by path at
// module level, but each component's useEnhanceDocument(doc) keeps its OWN
// useRef of the data. Saving from one screen (SaveDocumentButton -> update()
// -> refetch()) only ever updated that ref — so another screen that had
// already read the same path from the shared cache (e.g. DepartmentField
// reading employers/<id> after the Employer settings screen saved it) kept
// rendering the stale snapshot until a hard reload re-created the cache from
// scratch. Push the freshly refetched snapshot into both document caches too,
// so any screen that reads this path AFTER the save (a fresh mount, or the
// TTL-based wrapDocumentRefresh cache expiring) gets the updated value
// without needing a page reload. A screen already mid-render with its own
// ref keeps that ref until it re-mounts or calls refetch() itself — this
// does not touch the separate onSnapshot-based live listeners elsewhere
// (services/firebase/employers.js, useEmployerAttendanceData, outbox, …),
// which already push updates through their own subscriptions.
function refreshDocumentCaches(path, snapshot) {
  [documentCache, documentRefreshCache].forEach(cache => {
    const entry = cache[path]
    if (entry) {
      entry.promise.result = snapshot
      entry.promise.timeDone = Date.now()
    }
  })
}

export function useEnhanceDocument(doc) {
  const value = useRef(doc.data())
//  useEffect(() => doc.ref.onSnapshot(newValue => value.current = newValue.data()), [doc])
  const getPathSegments = () => String(doc.ref.path || '')
    .split('/')
    .filter(Boolean)

  return {
    data() {
      return value.current
    },
    async update(newValue) {
      await updateDocument(newValue, ...getPathSegments())
      value.current = newValue
    },
    async refetch() {
      doc = await getDocumentFromServer(...getPathSegments())
      value.current = doc.data()
      refreshDocumentCaches(getPathSegments().join('/'), doc)
      return doc
    }
  }
}

function useRead(promise) {
  if (!promise.done) {
    throw promise.throwable
  }

  return useEnhanceDocument(promise.result)
}

export const useDocument = (...pathParts) => wrapDocument(...pathParts).useRead()
export const useDocumentRefresh = (...pathParts) => wrapDocumentRefresh(...pathParts).useRead()
export const useCollection = (...pathParts) => wrapCollection(...pathParts).read()

// A wrapper whose read REJECTED must never be kept: `read()` re-throws the same
// error on every render, so the screen stays broken — "נסה שוב" re-renders into
// the identical cached failure — until the whole page is reloaded. The
// time-based eviction below cannot help there: `timeDone` is only stamped on
// success, so a failed entry was immortal.
function evictFailed(cache, cacheKey) {
  if (cache[cacheKey]?.promise?.failed) {
    delete cache[cacheKey]
  }
}

function checkCache(cache, cacheKey) {
  evictFailed(cache, cacheKey)
  const timeDone = cache[cacheKey]?.promise?.timeDone
  if (timeDone && Date.now() - timeDone > 3000) {
    delete cache[cacheKey]
  }
}

const _wrapDataFragments = (cache = {}) => function wrapDataFragments(refreshCache, ...paths) {
  const cacheKey = paths
    .map(path => Array.isArray(path)
      ? path.join('/')
      : path)
    .join(',')

  if (refreshCache) {
    checkCache(cache, cacheKey)
  } else {
    evictFailed(cache, cacheKey)
  }
  return cache[cacheKey] || (cache[cacheKey] = wrapPromise(getDataFragments(paths, refreshCache)))
}

const _wrapDataFragmentsCache = registerCache()
export const wrapDataFragments = _wrapDataFragments(_wrapDataFragmentsCache)

export const useDataFragments = (...paths) => wrapDataFragments(...paths).read()

export { getDocument, setDocument, updateDocument, deleteDocument }